import { useState } from 'react'
import { Link } from 'react-router-dom'
import BookingModal from '../components/BookingModal'
import FontAwesomeIcon from '../components/FontAwesomeIcon'

export default function MgahingaGorilla() {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedExperience, setSelectedExperience] = useState('')

  const handleBooking = (experience) => {
    setSelectedExperience(experience)
    setIsModalOpen(true)
  }

  const activities = [
    {
      name: 'Golden Monkey Tracking',
      icon: 'fa-paw',
      difficulty: 'Moderate',
      duration: '2-4 hours',
      price: 'From $100 per person',
      description: 'Track habituated golden monkeys in bamboo forests. These playful, endangered primates are found only in the Virunga region. Shorter and less strenuous than gorilla trekking but equally rewarding with excellent photo opportunities.',
      link: '/activities/golden-monkey-tracking'
    },
    {
      name: 'Volcano Climbing Adventures',
      icon: 'fa-volcano',
      difficulty: 'Strenuous to Very Strenuous',
      duration: '6-10 hours',
      price: 'From $80 per person',
      description: 'Summit one of three spectacular Virunga volcanoes: Mount Gahinga (3,474m, 6 hours, moderate), Mount Sabinyo (3,669m, 8 hours with ladders, strenuous), or Mount Muhabura (4,127m, 10 hours, very steep and challenging).',
      link: '/activities/mount-sabinyo-climb'
    },
    {
      name: 'Batwa Cultural Experience',
      icon: 'fa-users',
      difficulty: 'Easy to Moderate',
      duration: '3-4 hours',
      price: 'From $80 per person',
      description: 'Forest walks with indigenous Batwa pygmy guides who once lived in these mountains. Visit sacred Garama Cave, watch traditional fire-making and hunting demonstrations, and experience haunting cultural performances.',
      link: '/activities/mgahinga-batwa-culture'
    },
    {
      name: 'Border Trail Hikes',
      icon: 'fa-hiking',
      difficulty: 'Moderate',
      duration: '2-3 hours',
      price: 'From $40 per person',
      description: 'Scenic hikes along the Uganda-Rwanda-DRC tri-border with spectacular cross-country views. Traverse dramatic gorges and enjoy the unique perspective of standing at the junction of three nations.'
    }
  ]

  return (
    <div>
      {/* Hero Section */}
      <section
        className="relative h-80 md:h-96 bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(https://images.unsplash.com/photo-1551731409-43eb3e517a1a?w=1600)',
        }}
      >
        <div className="container mx-auto px-4 text-center text-white">
          <h1 className="text-3xl md:text-5xl font-bold mb-3 font-heading">
            Mgahinga Gorilla National Park
          </h1>
          <p className="text-base md:text-lg max-w-2xl mx-auto">
            Where Gorillas Meet Golden Monkeys in the Virunga Volcanoes
          </p>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl font-bold text-safari-green mb-4">Destination Overview</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Mgahinga Gorilla National Park is Uganda's smallest national park but one of its most spectacular. Nestled in
                the Virunga Mountains at the southwestern tip of Uganda, this 34 km² park forms part of the larger Virunga
                conservation area spanning Uganda, Rwanda, and the Democratic Republic of Congo. The park protects three
                volcanic peaks and is home to endangered mountain gorillas and the rare golden monkeys.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                The park's name derives from the local word "Gahinga" meaning piles of volcanic stones cleared from farmland.
                Rising from 2,227m to 4,127m at the summit of Mount Muhabura, Mgahinga offers dramatic volcanic landscapes,
                afro-alpine vegetation, and unique cultural experiences with the indigenous Batwa people. The park is a hidden
                gem for adventurers seeking both primate encounters and volcanic climbs.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Size</h3>
                  <p className="text-gray-700">34 km²</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Location</h3>
                  <p className="text-gray-700">Southwestern Uganda</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Elevation</h3>
                  <p className="text-gray-700">2,227m - 4,127m</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Volcanic Peaks</h3>
                  <p className="text-gray-700">3 Mountains</p>
                </div>
              </div>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1551731409-43eb3e517a1a?w=800"
                alt="Mgahinga Gorilla National Park"
                className="w-full h-96 object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How to Get There */}
      <section className="py-16 bg-safari-light">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">How to Get There</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-car" className="w-6 h-6" />
                  By Road from Kampala
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Distance:</strong> 540 km via Mbarara and Kabale</p>
                  <p><strong>Travel Time:</strong> 8-9 hours by private vehicle</p>
                  <p><strong>Route:</strong> Well-maintained highways through scenic southwestern Uganda highlands</p>
                  <p className="text-sm italic">Overnight stop in Kabale recommended for more relaxed journey.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-car" className="w-6 h-6" />
                  By Road from Kigali, Rwanda
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Distance:</strong> 140 km via Musanze</p>
                  <p><strong>Travel Time:</strong> 3-4 hours</p>
                  <p><strong>Best Option:</strong> Faster route for international travelers flying into Kigali</p>
                  <p className="text-sm italic">Popular choice combining Rwanda's Volcanoes NP with Mgahinga.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-plane" className="w-6 h-6" />
                  By Air
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Kisoro Airstrip:</strong> Charter flights from Entebbe or Kampala</p>
                  <p><strong>Flight Time:</strong> 1.5-2 hours</p>
                  <p><strong>Transfer:</strong> 14 km (30 minutes) from airstrip to park headquarters</p>
                  <p className="text-sm italic">Charter flights ideal for multi-destination safaris.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Highlights */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">Key Highlights</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-paw" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Golden Monkey Tracking</h3>
                <p className="text-gray-700">
                  Track rare golden monkeys found only in the Virunga region. These endangered primates inhabit bamboo forests
                  with playful behavior and striking golden-orange fur making exceptional photo subjects.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-volcano" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Volcano Climbing</h3>
                <p className="text-gray-700">
                  Summit three dramatic Virunga peaks: Mount Gahinga (3,474m), Mount Sabinyo (3,669m with thrilling ladders),
                  and Mount Muhabura (4,127m, highest). Each offers unique challenges and spectacular panoramic views.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-person-hiking" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Mountain Gorilla Trekking</h3>
                <p className="text-gray-700">
                  Visit the Nyakagezi gorilla family, the only habituated group in Mgahinga. Smaller crowds than Bwindi
                  with equally intimate encounters. Note: This family sometimes crosses into Rwanda.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-users" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Batwa Cultural Heritage</h3>
                <p className="text-gray-700">
                  Experience the culture of the Batwa pygmies, original forest dwellers. Visit Garama Cave, witness traditional
                  hunting techniques, fire-making, and hear their haunting music and stories.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-flag" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Tri-Country Border Experience</h3>
                <p className="text-gray-700">
                  Stand at the unique junction where Uganda, Rwanda, and Democratic Republic of Congo meet. Border Trail
                  offers spectacular cross-country vistas across the Virunga massif.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-seedling" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Unique Biodiversity</h3>
                <p className="text-gray-700">
                  Explore diverse afro-alpine vegetation zones from bamboo forests to heath and moorland. Home to endemic
                  birds, duikers, forest elephants, and giant lobelia at higher elevations.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Activities & Experiences */}
      <section className="py-16 bg-safari-light">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">Activities & Experiences</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {activities.map((activity, index) => (
              <div key={index} className="card group">
                <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                  <FontAwesomeIcon icon={activity.icon} className="w-20 h-20 text-white" />
                </div>
                <div className="p-6 flex flex-col flex-grow">
                  <h3 className="text-lg font-bold text-safari-green mb-2">{activity.name}</h3>
                  <p className="text-gray-700 mb-4 flex-grow">
                    {activity.description}
                  </p>
                  <div className="space-y-2 mb-4 text-sm text-gray-600">
                    <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: {activity.duration}</p>
                    <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: {activity.difficulty}</p>
                  </div>
                  <p className="text-safari-gold font-bold mb-3 text-center text-lg">{activity.price}</p>
                  {activity.link ? (
                    <Link to={activity.link} className="btn-primary w-full text-center block">
                      View Experience
                    </Link>
                  ) : (
                    <button onClick={() => handleBooking(`${activity.name} - Mgahinga`)} className="btn-primary w-full">
                      Book Experience
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Travel Tips */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">Travel Tips</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-calendar" className="w-6 h-6" />
                  Best Time to Visit
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Dry Seasons (Best):</strong> June-September and December-February. Easier hiking trails and clearer mountain views.</p>
                  <p><strong>Wet Seasons:</strong> March-May and October-November. More challenging treks but lush vegetation and fewer tourists.</p>
                  <p><strong>Year-Round Activity:</strong> Gorilla and golden monkey tracking available all year with advance permit booking.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-suitcase" className="w-6 h-6" />
                  What to Pack
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Essential:</strong> Waterproof hiking boots, rain jacket, long trousers, long-sleeved shirts for forest protection</p>
                  <p><strong>Layers:</strong> Warm clothing for cold mountain mornings and evenings. Temperature varies with altitude.</p>
                  <p><strong>Gear:</strong> Walking stick (can be rented), gardening gloves for grasping vegetation, daypack</p>
                  <p><strong>Optional:</strong> Gaiters for muddy trails, camera with good zoom, insect repellent</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-ticket" className="w-6 h-6" />
                  Permits & Pricing
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Gorilla Permits:</strong> $800 per person. Book 3-6 months in advance through UWA or tour operators.</p>
                  <p><strong>Golden Monkey Permits:</strong> $100 per person. Generally available on shorter notice.</p>
                  <p><strong>Volcano Climbing:</strong> $80 per mountain. Can be booked at park headquarters.</p>
                  <p><strong>Batwa Trail:</strong> $80 per person. Community-based tourism supporting indigenous people.</p>
                  <p className="text-sm italic">Foreign Non-Residents pay rates listed above. East African residents have different rates.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-heartbeat" className="w-6 h-6" />
                  Health & Fitness
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Fitness Required:</strong> Moderate to high fitness for volcano climbs. Basic fitness sufficient for golden monkeys.</p>
                  <p><strong>Altitude:</strong> Park ranges 2,227m-4,127m. Acclimatization recommended for high-altitude climbs.</p>
                  <p><strong>Health Precautions:</strong> Yellow fever vaccination required. Malaria prophylaxis recommended.</p>
                  <p><strong>Age Limits:</strong> Minimum age 15 for gorilla tracking, 12 for golden monkeys, no age limit for cultural experiences.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Where to Stay */}
      <section className="py-16 bg-safari-light">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-6 text-center">Where to Stay</h2>
          <p className="text-center text-gray-700 mb-12 max-w-3xl mx-auto">
            Accommodation options around Mgahinga range from luxury lodges to community-run camps, most located in nearby Kisoro town or near the park gate.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-lg shadow-lg">
              <thead className="bg-safari-green text-white">
                <tr>
                  <th className="px-6 py-4 text-left">Accommodation</th>
                  <th className="px-6 py-4 text-left">Category</th>
                  <th className="px-6 py-4 text-left">Price/Night</th>
                  <th className="px-6 py-4 text-left">Highlights</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold">Mount Gahinga Lodge</td>
                  <td className="px-6 py-4 text-safari-green font-bold">Luxury</td>
                  <td className="px-6 py-4">$400-600</td>
                  <td className="px-6 py-4">Premium banda cottages with fireplaces, stunning mountain views, gourmet dining</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold">Amajambere Iwacu Camp</td>
                  <td className="px-6 py-4 text-safari-green font-bold">Mid-range</td>
                  <td className="px-6 py-4">$150-250</td>
                  <td className="px-6 py-4">Community-owned eco-camp, cottages and tents, supports local Batwa community</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold">Travellers Rest Hotel</td>
                  <td className="px-6 py-4 text-safari-green font-bold">Budget</td>
                  <td className="px-6 py-4">$60-100</td>
                  <td className="px-6 py-4">Historic Kisoro hotel where Dian Fossey stayed, garden setting, restaurant</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">Frequently Asked Questions</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">What's the difference between Mgahinga and Bwindi for gorilla trekking?</h3>
                <p className="text-gray-700">
                  Mgahinga has only 1 habituated gorilla family (Nyakagezi) versus Bwindi's 19+ families, meaning fewer visitors and more exclusive
                  experience. However, the Nyakagezi family occasionally crosses into Rwanda. Mgahinga also offers golden monkeys and volcano climbing
                  that Bwindi doesn't have.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Are golden monkeys endangered?</h3>
                <p className="text-gray-700">
                  Yes, golden monkeys are listed as endangered. They're found only in the Virunga region and Gishwati forest in Rwanda. Estimated
                  population is around 3,000-4,000 individuals. Mgahinga has about 40-60 habituated golden monkeys across two groups.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Which volcano should I climb?</h3>
                <p className="text-gray-700">
                  Mount Gahinga (3,474m, 6 hours) is moderate and good for first-timers. Mount Sabinyo (3,669m, 8 hours) offers thrilling ladder
                  sections and tri-country border summit. Mount Muhabura (4,127m, 10 hours) is the highest and most challenging with steep terrain
                  but spectacular 360-degree views.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">How difficult is the Batwa Trail?</h3>
                <p className="text-gray-700">
                  The Batwa Cultural Trail is relatively easy to moderate, suitable for most fitness levels. It involves gentle forest walking
                  to Garama Cave with Batwa guides. More culturally focused than physically demanding. Duration is 3-4 hours including demonstrations.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Can I combine Mgahinga with Rwanda or DRC parks?</h3>
                <p className="text-gray-700">
                  Absolutely! Mgahinga is only 3-4 hours from Kigali and borders Rwanda's Volcanoes National Park and DRC's Virunga National Park.
                  Many travelers combine gorilla trekking across multiple countries. Cross-border permits and logistics can be arranged through
                  tour operators.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">What's the best time to visit?</h3>
                <p className="text-gray-700">
                  Dry seasons (June-September, December-February) offer easier trails and better weather for volcano climbing. However, Mgahinga
                  is accessible year-round. Wet season means fewer tourists, lush vegetation, and potentially lower accommodation rates.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">How many days should I spend at Mgahinga?</h3>
                <p className="text-gray-700">
                  Minimum 2-3 days recommended to experience golden monkeys, one volcano climb, and Batwa cultural experience. Add extra day for
                  gorilla trekking. Many visitors spend 3-4 days combining multiple activities without rushing.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Is altitude sickness a concern?</h3>
                <p className="text-gray-700">
                  Park base is at 2,227m with volcanos reaching 4,127m. Mild altitude effects (headache, fatigue) possible on high climbs.
                  Stay hydrated, ascend slowly, and acclimatize if climbing Muhabura. Most visitors have no issues with golden monkey tracking
                  at lower elevations.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-safari-green text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Discover the Hidden Gem of the Virunga Volcanoes
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Experience rare golden monkeys, mountain gorillas, and volcanic adventures in Uganda's most compact yet
            spectacular national park. Mgahinga offers intimate wildlife encounters and dramatic landscapes where three
            countries meet.
          </p>
          <button
            onClick={() => handleBooking('Complete Mgahinga Experience')}
            className="btn-primary text-lg"
          >
            Plan Your Mgahinga Adventure
          </button>
        </div>
      </section>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        experience={selectedExperience}
      />
    </div>
  )
}
